import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Camera, SwitchCamera, X } from 'lucide-react';
import { cn } from '../../lib/utils';

// 1D barcode formats only — no QR/DataMatrix to avoid reading QR codes
// on labels that have both (QR often contains JSON metadata, not the barcode number)
const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.CODABAR,
];

interface BarcodeScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScan: (code: string) => void;
}

export function BarcodeScannerDialog({ open, onOpenChange, onScan }: BarcodeScannerDialogProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const containerId = 'barcode-scanner-region';

  const handleScan = useCallback((code: string) => {
    if (scannedRef.current) return;
    scannedRef.current = true;
    onScan(code);
    onOpenChange(false);
  }, [onScan, onOpenChange]);

  useEffect(() => {
    if (!open) {
      scannedRef.current = false;
      return;
    }

    let mounted = true;
    setError(null);

    const startScanner = async () => {
      // Wait for container to be in DOM
      const container = document.getElementById(containerId);
      if (!container) return;

      try {
        const scanner = new Html5Qrcode(containerId, {
          formatsToSupport: SUPPORTED_FORMATS,
          verbose: false,
        });
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode },
          {
            fps: 20,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              // Wide rectangle optimized for 1D barcodes — 90% width, short height
              const w = Math.floor(viewfinderWidth * 0.9);
              const h = Math.min(Math.floor(viewfinderHeight * 0.3), 150);
              return { width: Math.max(w, 200), height: Math.max(h, 100) };
            },
            disableFlip: false,
          },
          (decodedText) => {
            if (!mounted) return;
            handleScan(decodedText);
          },
          () => {
            // No code detected yet — normal, keep scanning
          }
        );
      } catch (err) {
        if (!mounted) return;
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes('Permission') || message.includes('NotAllowed')) {
          setError('Permissão da câmera negada. Permita o acesso nas configurações do navegador.');
        } else if (message.includes('NotFound') || message.includes('device')) {
          setError('Nenhuma câmera encontrada neste dispositivo.');
        } else {
          setError(`Erro ao acessar câmera: ${message}`);
        }
      }
    };

    const timer = setTimeout(startScanner, 200);

    return () => {
      mounted = false;
      clearTimeout(timer);
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [open, facingMode, handleScan]);

  const toggleCamera = async () => {
    if (scannerRef.current) {
      await scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] md:max-w-md p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Camera className="h-5 w-5" />
            Escanear Código de Barras
          </DialogTitle>
          <DialogDescription className="text-xs">
            Aponte a câmera para o código de barras do produto
          </DialogDescription>
        </DialogHeader>

        <div className="relative bg-black">
          <div
            id={containerId}
            className={cn(
              "w-full aspect-square md:aspect-video",
              "[&_video]:w-full [&_video]:object-cover",
              "[&_img]:hidden"
            )}
          />

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-6">
              <div className="text-center space-y-3">
                <X className="h-10 w-10 mx-auto text-destructive" />
                <p className="text-sm text-white">{error}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-3 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleCamera}
            className="gap-1.5"
          >
            <SwitchCamera className="h-4 w-4" />
            Trocar Câmera
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
